import type { ReactNode, JSX } from 'react'
import { useEffect, useMemo } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import type { DutyStatus } from '@/features/duties/types'
import { isDutyStatus } from '@/features/duties/statusMachine'
import { CompositeTabScope } from '@/components/layout/CompositeTabScope'
import { PatientsList } from '@/components/PatientsList'
import { AppointmentsList } from '@/components/AppointmentsList'
import { Providers } from '@/components/Providers'
import { Visits } from '@/components/Visits'
import { ClaimsList } from '@/features/claims/components/ClaimsList'
import { DocumentsList } from '@/components/DocumentsList'
import { MeetingNotesList } from '@/features/meetings/components/MeetingNotesList'
import { lazyWithReload } from '@/lib/lazyWithReload'
import { ConfigManager } from '@/components/ConfigManager'
import { ToolsAdminPage } from '@/features/tools/components/ToolsAdminPage/ToolsAdminPage'
import { SigningConfigurationPage } from '@/features/signing/SigningConfigurationPage'
import { SigningDocumentsPage } from '@/features/signing-documents/SigningDocumentsPage'
import { PermissionsMatrixPage } from '@/features/permissions'
import { ProtectedCapabilityRoute } from '@/features/permissions/components/ProtectedCapabilityRoute'
import { ProtectedPermissionsRoute } from '@/features/permissions/components/ProtectedPermissionsRoute'
import { FilesPage } from '@/features/files'
import { IngestionRunsPage } from '@/features/ingestion'
import { useDocumentEditor } from '@/features/documents'
import { useSelectedAgent } from '@/contexts/SelectedAgentContext'
import { QueryResultsRoute } from '@/components/QueryResultsRoute'
import { AllWorkflowRunsListView, SessionDetail } from '@/features/sessions'
import { MachinesGrid } from '@/features/machines'
import { PhoneLineBoardPage } from '@/features/phone-lines'
import { WorkflowDetail } from '@/features/workflows'
import { ConnectionsPage } from '@/features/connections'
import { useConfiguration } from '@/hooks/useConfiguration'
import { useSnippetsEnabled } from '@/hooks/useSnippetsEnabled'
import { CallLogs } from '@/features/calls/components/CallLogs'
import { EvalDutyDetail } from '@/features/evals/components/EvalDutyDetail'
import { EvalCaseDetailPage } from '@/features/evals/components/EvalCaseDetailPage'
import { ProtectedFeatureRoute } from '@/components/ProtectedFeatureRoute'
import { ProtectedGlobalAdminRoute } from '@/components/ProtectedGlobalAdminRoute'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { BillingPage } from '@/features/billing/components/BillingPage'
import { FactCatalogue } from '@/features/facts'
import { MessagesList } from '@/features/messages'
import {
  ChronicConditionsList,
  LabResultsList,
} from '@/features/clinical-data/components/ClinicalDataList'
import { ProtectedCareforceOrgRoute } from '@/components/ProtectedCareforceOrgRoute'
import {
  EVALS_ENABLED,
  matchAppRoute,
  routeById,
  type AppRoute,
  type RouteParams,
} from './routeManifest'

// Route-scoped heavy features, kept off the boot path. These carry the bundle
// weight nothing else needs: the Milkdown/ProseMirror editor (duty cards, docs,
// notes, snippets), the metrics dashboards, and the call recording player.
// Every one of them renders inside TabPanel (ContentPanelAreaInner.tsx), which
// owns both halves of the contract, so no route-local boundary is needed:
// Suspense covers the PENDING import, and a per-tab error boundary catches a
// REJECTED one. Suspense alone never did — a rejected import is an error.
// displayName keeps the characterization guard chains readable.
const Duties = lazyWithReload('Duties', () =>
  import('@/components/Duties').then((m) => ({ default: m.Duties }))
)
const DutyCallPage = lazyWithReload('DutyCallPage', () =>
  import('@/components/DutyCallPage').then((m) => ({ default: m.DutyCallPage }))
)
const DutyWorkflowCanvas = lazyWithReload('DutyWorkflowCanvas', () =>
  import('@/components/DutyWorkflowCanvas').then((m) => ({
    default: m.DutyWorkflowCanvas,
  }))
)
const DutyClientsPage = lazyWithReload('DutyClientsPage', () =>
  import('@/features/duties/components/DutyClients/DutyClientsPage').then(
    (m) => ({ default: m.DutyClientsPage })
  )
)
const DutyRobocallPage = lazyWithReload('DutyRobocallPage', () =>
  import('@/components/DutyRobocallPage').then((m) => ({
    default: m.DutyRobocallPage,
  }))
)
const DutyTextingPage = lazyWithReload('DutyTextingPage', () =>
  import('@/components/DutyTextingPage').then((m) => ({
    default: m.DutyTextingPage,
  }))
)
const NotificationsPage = lazyWithReload('NotificationsPage', () =>
  import('@/features/notifications').then((m) => ({
    default: m.NotificationsPage,
  }))
)
const MemoryEvalsPage = lazyWithReload('MemoryEvalsPage', () =>
  import('@/features/memory-evals').then((m) => ({
    default: m.MemoryEvalsPage,
  }))
)
const MemoryEvalCaseDetailPage = lazyWithReload(
  'MemoryEvalCaseDetailPage',
  () =>
    import('@/features/memory-evals').then((m) => ({
      default: m.MemoryEvalCaseDetailPage,
    }))
)
const DutiesMetricsPage = lazyWithReload('DutiesMetricsPage', () =>
  import('@/features/duties-metrics/components/DutiesMetricsPage').then(
    (m) => ({
      default: m.DutiesMetricsPage,
    })
  )
)
const DocumentEditorPanel = lazyWithReload('DocumentEditorPanel', () =>
  import('@/features/documents/components/DocumentEditorPanel').then((m) => ({
    default: m.DocumentEditorPanel,
  }))
)
const MeetingNotesViewer = lazyWithReload('MeetingNotesViewer', () =>
  import('@/features/meetings/components/MeetingNotesViewer').then((m) => ({
    default: m.MeetingNotesViewer,
  }))
)
const MeetingTranscriptViewer = lazyWithReload('MeetingTranscriptViewer', () =>
  import('@/features/meetings/components/MeetingTranscriptViewer').then(
    (m) => ({
      default: m.MeetingTranscriptViewer,
    })
  )
)
const SnippetsPage = lazyWithReload('SnippetsPage', () =>
  import('@/features/snippets/SnippetsPage').then((m) => ({
    default: m.SnippetsPage,
  }))
)
const EvaluationRubricsPage = lazyWithReload('EvaluationRubricsPage', () =>
  import('@/features/evaluation-rubrics/EvaluationRubricsPage').then((m) => ({
    default: m.EvaluationRubricsPage,
  }))
)
const CallDetailPage = lazyWithReload('CallDetailPage', () =>
  import('@/features/calls/components/CallDetailPage').then((m) => ({
    default: m.CallDetailPage,
  }))
)

interface MainRouteConfig {
  title: string
  /**
   * Title to call `renameTab` with from the bridge. Set when the title is
   * derived from nav state (e.g. a duty name passed via navigation); leave
   * undefined when the title is a fallback placeholder. The bridge skips the
   * rename when this is undefined, which prevents a real name from being
   * overwritten by a placeholder on a subsequent navigation that lost state.
   */
  authoritativeTitle?: string
  getElement: () => ReactNode
}

function getPathSegments(pathname: string) {
  return pathname.split('/').filter(Boolean)
}

type DutyNavState = {
  dutyName?: string
  patientCount?: number | null
  callCount?: number | null
  computerUse?: boolean
  status?: DutyStatus
  tabTitle?: string
} | null

function parseDutyNavState(value: unknown): DutyNavState {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const v = value as Record<string, unknown>

  const out: NonNullable<DutyNavState> = {}

  const dutyName =
    typeof v.dutyName === 'string' && v.dutyName.trim().length > 0
      ? v.dutyName.trim()
      : undefined
  if (dutyName) out.dutyName = dutyName

  const patientCount =
    typeof v.patientCount === 'number' || v.patientCount === null
      ? (v.patientCount as number | null)
      : undefined
  if (patientCount !== undefined) out.patientCount = patientCount

  const callCount =
    typeof v.callCount === 'number' || v.callCount === null
      ? (v.callCount as number | null)
      : undefined
  if (callCount !== undefined) out.callCount = callCount

  if (typeof v.computerUse === 'boolean') out.computerUse = v.computerUse

  if (isDutyStatus(v.status)) {
    out.status = v.status
  }

  const tabTitle =
    typeof v.tabTitle === 'string' && v.tabTitle.trim().length > 0
      ? v.tabTitle.trim()
      : undefined
  if (tabTitle) out.tabTitle = tabTitle

  return Object.keys(out).length > 0 ? out : null
}

function parseEvalNavState(value: unknown): { caseName?: string } | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const v = value as Record<string, unknown>
  const caseName =
    typeof v.caseName === 'string' && v.caseName.trim().length > 0
      ? v.caseName.trim()
      : undefined
  return caseName ? { caseName } : null
}

function DutyDiagramRoute({ dutyId }: { dutyId: string }) {
  const location = useLocation()
  const state = useMemo(
    () => parseDutyNavState(location.state),
    [location.state]
  )

  return (
    <DutyWorkflowCanvas
      dutyId={dutyId}
      dutyName={state?.dutyName}
      patientCount={state?.patientCount ?? null}
      callCount={state?.callCount ?? null}
      computerUse={state?.computerUse}
      status={state?.status}
    />
  )
}

function DutyCallRoute({
  dutyId,
  activeTab,
}: {
  dutyId: string
  activeTab: 'call' | 'playground'
}) {
  const location = useLocation()
  const state = useMemo(
    () => parseDutyNavState(location.state),
    [location.state]
  )

  return (
    <DutyCallPage
      dutyId={dutyId}
      dutyName={state?.dutyName}
      tabTitle={state?.tabTitle}
      activeTab={activeTab}
    />
  )
}

function DutyRobocallRoute({ dutyId }: { dutyId: string }) {
  const location = useLocation()
  const state = useMemo(
    () => parseDutyNavState(location.state),
    [location.state]
  )

  return (
    <ErrorBoundary>
      <DutyRobocallPage
        dutyId={dutyId}
        dutyName={state?.dutyName}
        tabTitle={state?.tabTitle}
      />
    </ErrorBoundary>
  )
}

function DutyClientsRoute({ dutyId }: { dutyId: string }) {
  const location = useLocation()
  const state = useMemo(
    () => parseDutyNavState(location.state),
    [location.state]
  )

  return (
    <ErrorBoundary>
      <DutyClientsPage dutyId={dutyId} dutyName={state?.dutyName} />
    </ErrorBoundary>
  )
}

function DutyTextingRoute({ dutyId }: { dutyId: string }) {
  const location = useLocation()
  const state = useMemo(
    () => parseDutyNavState(location.state),
    [location.state]
  )

  return (
    <ErrorBoundary>
      <DutyTextingPage dutyId={dutyId} dutyName={state?.dutyName} />
    </ErrorBoundary>
  )
}

function capabilityRoute(
  capability: string,
  element: JSX.Element
): JSX.Element {
  return (
    <ProtectedCapabilityRoute capability={capability}>
      {element}
    </ProtectedCapabilityRoute>
  )
}

function SessionDetailRoute() {
  const location = useLocation()
  const segments = getPathSegments(location.pathname)
  const sessionId = segments[1] // /sessions/[sessionId]

  if (!sessionId) return null

  return <SessionDetail sessionId={sessionId} />
}

function parseWorkflowNavState(
  value: unknown
): { workflowTitle?: string } | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const v = value as Record<string, unknown>
  const workflowTitle =
    typeof v.workflowTitle === 'string' && v.workflowTitle.trim().length > 0
      ? v.workflowTitle.trim()
      : undefined
  return workflowTitle ? { workflowTitle } : null
}

// Search is carried over: `?agent=` selects the agent app-wide (see App.tsx),
// so dropping it here would silently reset it on an old link.
function WorkflowsRedirect() {
  const location = useLocation()

  return (
    <Navigate
      to={{ pathname: '/connections', search: location.search }}
      replace
    />
  )
}

function WorkflowDetailRoute() {
  const location = useLocation()
  const segments = getPathSegments(location.pathname)
  const slug = segments[1] // /workflows/[slug]

  if (!slug) return null

  return <WorkflowDetail slug={slug} />
}

function DocsDetailRoute() {
  const location = useLocation()
  const { selectedAgentId } = useSelectedAgent()
  const { isLoading, initialData, openEditor } = useDocumentEditor()

  const segments = getPathSegments(location.pathname)
  const rawId = segments[0] === 'docs' ? segments[1] : undefined
  // Creation happens via the modal/create action, not a /docs/new route; ignore
  // it so a stray link doesn't fetch a non-existent "new" document.
  const docId = rawId === 'new' ? undefined : rawId

  useEffect(() => {
    if (docId) void openEditor(docId)
  }, [docId, openEditor])

  if (!docId) return null

  return (
    <DocumentEditorPanel
      agentId={selectedAgentId}
      documentId={docId}
      initialData={initialData}
      isLoading={isLoading}
    />
  )
}

function NotesRoute() {
  const location = useLocation()

  const segments = getPathSegments(location.pathname)
  const id = segments[1]

  if (!id) {
    return <MeetingNotesList />
  }
  // Meeting note detail view uses the editable MeetingNotesViewer (Milkdown editor).
  return <MeetingNotesViewer meetingId={id} />
}

function TranscriptsRoute() {
  const location = useLocation()
  const segments = getPathSegments(location.pathname)
  const id = segments[1]

  if (!id) {
    return null
  }

  return <MeetingTranscriptViewer meetingNotesId={id} />
}

function SnippetsRoute() {
  const { loading, isPreviousOrgData } = useConfiguration()
  const enabled = useSnippetsEnabled()

  // "Still resolving" must not render the same blank panel as "off for this
  // org". During an org switch placeholderData serves the previous org's config,
  // so `loading` is already false while useSnippetsEnabled correctly reports
  // false — gating on `!enabled` alone whited out the tab until the new org's
  // config landed. SnippetsPage carries its own skeleton for this.
  if (!loading && !isPreviousOrgData && !enabled) {
    return null
  }

  return <SnippetsPage />
}

type RouteConfigFactory = (ctx: {
  params: RouteParams
  state: unknown
}) => MainRouteConfig | null

// Factory for the common case: a static title and a static element.
const page =
  (title: string, el: () => ReactNode): RouteConfigFactory =>
  () => ({ title, getElement: el })

/**
 * Governance wrappers composed from the manifest entry, innermost first:
 * admin(featureFlag(capability(element))). The characterization chains pin
 * this order (the feature gate OUTSIDE the capability gate for
 * duties.metrics). Non-governance wrappers (ErrorBoundary, sub-view gates)
 * stay in factories.
 */
function applyGuards(route: AppRoute, element: ReactNode): ReactNode {
  let el = element as JSX.Element
  if (route.capability) el = capabilityRoute(route.capability, el)
  if (route.featureFlag) {
    el = (
      <ProtectedFeatureRoute featureFlag={route.featureFlag}>
        {el}
      </ProtectedFeatureRoute>
    )
  }
  if (route.admin === 'global') {
    el = <ProtectedGlobalAdminRoute>{el}</ProtectedGlobalAdminRoute>
  }
  if (route.admin === 'permissions') {
    el = <ProtectedPermissionsRoute>{el}</ProtectedPermissionsRoute>
  }
  if (route.admin === 'careforceOrg') {
    el = <ProtectedCareforceOrgRoute>{el}</ProtectedCareforceOrgRoute>
  }
  return el
}

// The duty.detail entry's subViews list in routeManifest.ts mirrors these
// keys (pinned in mainRoutes.characterization.test.tsx). An unknown or
// missing sub-view falls back to the diagram.
export const dutySubViewElements: Record<
  string,
  (dutyId: string) => ReactNode
> = {
  call: (dutyId) => <DutyCallRoute dutyId={dutyId} activeTab="call" />,
  playground: (dutyId) => (
    <DutyCallRoute dutyId={dutyId} activeTab="playground" />
  ),
  // Sub-view gate, not entry governance: the entry spans all duty sub-views,
  // and only this one lists patient records.
  clients: (dutyId) =>
    capabilityRoute('duty.clients.view', <DutyClientsRoute dutyId={dutyId} />),
  robocall: (dutyId) => <DutyRobocallRoute dutyId={dutyId} />,
  texting: (dutyId) => <DutyTextingRoute dutyId={dutyId} />,
}

// One factory per manifest entry. Titles and non-governance composition live
// here; guards come from the entry via applyGuards. Params arrive raw
// (react-router v7 matchPath does not percent-decode), matching the old
// switch's raw-segment reads — pinned in mainRoutes.characterization.test.tsx.
export const routeConfigs: Record<string, RouteConfigFactory> = {
  'snippets.list': page('Snippets', () => <SnippetsRoute />),
  'duties.list': page('Duties', () => <Duties />),
  'duties.metrics': page('Metrics', () => (
    <ErrorBoundary>
      <DutiesMetricsPage />
    </ErrorBoundary>
  )),
  'duty.detail': ({ params, state }) => {
    const dutyId = params.dutyId
    if (!dutyId) return null
    const navState = parseDutyNavState(state)
    const dutyName = navState?.dutyName ?? null
    const sub = params['*']?.split('/')[0] ?? ''
    // hasOwn: sub is URL-controlled; a plain index read would find inherited
    // Object.prototype members ('constructor', …) instead of the fallback.
    const element = Object.hasOwn(dutySubViewElements, sub)
      ? dutySubViewElements[sub]
      : undefined
    return {
      title: dutyName ?? `Duty (${dutyId})`,
      authoritativeTitle: dutyName ?? undefined,
      // One scope for the WHOLE duty tab, not per sub-view: the sub-views swap
      // beneath it, so a card in any of them registers under the same tab id
      // and the tab keeps a single close owner.
      getElement: () => (
        <CompositeTabScope>
          {element ? element(dutyId) : <DutyDiagramRoute dutyId={dutyId} />}
        </CompositeTabScope>
      ),
    }
  },
  'call.detail': ({ params }) => {
    const callId = params.callId
    if (!callId) return null
    return {
      title: `Call ${callId.slice(0, 8)}`,
      getElement: () => <CallDetailPage callId={callId} />,
    }
  },
  'calls.list': page('Call Logs', () => <CallLogs />),
  'messages.list': page('Messages', () => <MessagesList />),
  'signing.documents': page('Documents', () => <SigningDocumentsPage />),
  'data.patients': page('Clients List', () => <PatientsList />),
  'data.appointments': page('Appointments', () => <AppointmentsList />),
  'data.claims': page('Claims', () => (
    <ErrorBoundary>
      <ClaimsList />
    </ErrorBoundary>
  )),
  'data.providers': page('Providers', () => <Providers />),
  'data.visits': page('Visits', () => <Visits />),
  'data.labResults': page('Lab Results', () => (
    <ErrorBoundary>
      <LabResultsList />
    </ErrorBoundary>
  )),
  'data.chronicConditions': page('Chronic Conditions', () => (
    <ErrorBoundary>
      <ChronicConditionsList />
    </ErrorBoundary>
  )),
  'data.files': page('Files', () => <FilesPage />),
  'data.ingestion': page('Imports', () => <IngestionRunsPage />),
  'data.facts': page('Facts Catalogue', () => <FactCatalogue />),
  notifications: page('Notifications', () => (
    <ErrorBoundary>
      <NotificationsPage />
    </ErrorBoundary>
  )),
  'docs.list': page('Documents', () => <DocumentsList />),
  // Placeholder title until the editor resolves the doc name and renames the tab.
  'doc.detail': page('Document', () => <DocsDetailRoute />),
  'notes.list': page('Notes', () => <NotesRoute />),
  queryResults: page('Query Results', () => <QueryResultsRoute />),
  'transcript.detail': page('Meeting Transcript', () => <TranscriptsRoute />),
  'note.detail': page('Meeting Notes', () => <NotesRoute />),
  billing: page('Billing', () => <BillingPage />),
  config: page('Config', () => <ConfigManager />),
  tools: page('Tools', () => <ToolsAdminPage />),
  'signing.form': ({ params }) => ({
    title: params.formKey?.toUpperCase() ?? 'Document signing',
    getElement: () => (
      <SigningConfigurationPage
        formKey={params.formKey ?? ''}
        configurationId={params.configurationId}
      />
    ),
  }),
  permissions: page('Permissions', () => <PermissionsMatrixPage />),
  connections: page('Connections', () => <ConnectionsPage />),
  machines: page('Machines', () => <MachinesGrid />),
  evaluationRubrics: page('Evaluation Rubrics', () => (
    <EvaluationRubricsPage />
  )),
  // Boundary at the route (per CLAUDE.md) so a bad row in the 2s poll can't
  // take down the whole app shell — the only other boundary is App-wide.
  phoneLines: page('Active Lines', () => (
    <ErrorBoundary>
      <PhoneLineBoardPage />
    </ErrorBoundary>
  )),
  'sessions.list': page('Sessions', () => <AllWorkflowRunsListView />),
  'session.detail': ({ params }) => {
    const sessionId = params.sessionId
    if (!sessionId) return null
    return {
      // Tab title: "Session 74ddd3b1"
      title: `Session ${sessionId.slice(0, 8)}`,
      getElement: () => <SessionDetailRoute />,
    }
  },
  // The workflows list now lives at the bottom of Connections, so the old
  // page redirects instead of 404-ing: it is a bookmarked route. The manifest
  // puts it on Connections' tab, so the redirect never opens one of its own.
  'workflows.list': page('Connections', () => <WorkflowsRedirect />),
  'workflow.detail': ({ params, state }) => {
    const slug = params.slug
    if (!slug) return null
    const navState = parseWorkflowNavState(state)
    return {
      title: navState?.workflowTitle ?? `Workflow (${slug})`,
      authoritativeTitle: navState?.workflowTitle ?? undefined,
      getElement: () => <WorkflowDetailRoute />,
    }
  },
  memoryEvals: page('Evals', () => <MemoryEvalsPage />),
  'memoryEvalCase.detail': ({ params, state }) => {
    const caseId = params.caseId
    if (!caseId) return null
    const caseName = parseEvalNavState(state)?.caseName
    return {
      title: caseName ?? 'Memory Eval Case',
      authoritativeTitle: caseName,
      getElement: () => <MemoryEvalCaseDetailPage caseId={caseId} />,
    }
  },
  // Mirrors the manifest's EVALS_ENABLED gate so registry and manifest stay
  // in exact-set parity under either setting.
  ...(EVALS_ENABLED
    ? ({
        'evalCase.detail': ({ params, state }) => {
          const { dutyId, caseId } = params
          if (!dutyId || !caseId) return null
          const navState = parseEvalNavState(state)
          return {
            title: navState?.caseName ?? 'Eval Test Case',
            // Same single-close-owner scope as the duty tab; an eval case is
            // the prompt card plus its config cards, edited together.
            getElement: () => (
              <CompositeTabScope>
                <EvalCaseDetailPage dutyId={dutyId} caseId={caseId} />
              </CompositeTabScope>
            ),
          }
        },
        'evalDuty.testCases': ({ params }) => {
          const dutyId = params.dutyId
          if (!dutyId) return null
          return {
            title: 'Evaluations',
            getElement: () => <EvalDutyDetail dutyId={dutyId} />,
          }
        },
        'evalDuty.detail': ({ params }) => {
          const dutyId = params.dutyId
          if (!dutyId) return null
          return {
            title: 'Evaluations',
            getElement: () => <EvalDutyDetail dutyId={dutyId} />,
          }
        },
      } satisfies Record<string, RouteConfigFactory>)
    : {}),
}

/**
 * Guarded element for an already-matched route. AppRouteHost calls this once
 * per tab with the params from the manifest match, so a tab renders from its
 * own routeId instead of the live URL. Titles still come from
 * getMainRouteConfigForLocation below.
 */
export function renderRouteElement(
  routeId: string,
  params: RouteParams,
  state: unknown
): ReactNode {
  const route = routeById(routeId)
  if (!route) return null
  const config = routeConfigs[routeId]?.({ params, state })
  if (!config) return null
  return applyGuards(route, config.getElement())
}

// Home is not a tab. Visiting `/` matches no manifest entry, so AppShellEffects
// opens no tab for it; the Home view renders directly in the content area when
// `tabs.length === 0`.
export function getMainRouteConfigForLocation(location: {
  pathname: string
  state?: unknown
}): MainRouteConfig | null {
  const matched = matchAppRoute(location.pathname)
  if (!matched) return null
  const config = routeConfigs[matched.route.routeId]?.({
    params: matched.params,
    state: location.state,
  })
  if (!config) return null
  return {
    ...config,
    // Titles come from the factory, but the element delegates to the live
    // render path, so the characterization guard matrix proves what production
    // renders instead of a second composition of the same guards.
    getElement: () =>
      renderRouteElement(matched.route.routeId, matched.params, location.state),
  }
}
